import MsDyn365, { IActionContext, ITelemetry, ICreateActionContext, IGeneric, IAny, IRequestContext } from '@msdyn365-commerce/core';
import { ITelemetryContent } from '@msdyn365-commerce-modules/utilities';
import { OrgUnitLocation, ProductSearchResult, SimpleProduct } from '@msdyn365-commerce/retail-proxy';
import { ICommerceApiSettings, IComponentProps } from '@msdyn365-commerce/core-internal';
import storeSelectorStateDataAction from '../modules/mmx-store-selector/state-manager/store-selector-state-manager-data-action';
import { IStoreSelectorStateManager, MapStoreSelectorStateManager } from '../modules/mmx-store-selector/state-manager';
import {
    GenericInput,
    getDeliveryOptionsForSelectedVariant,
    GetDeliveryOptionsForSelectedVariantInput,
    getOrgUnitLocationsByArea,
    GetOrgUnitLocationsByAreaInput,
    generateProductImageUrl as defaultGenerateProductImageUrl
} from '@msdyn365-commerce-modules/retail-actions';
import React from 'react';

export const CURRENCY_DECIMAL_PLACES: number = 2;

export const PRICE_FREE: number = 0;

export enum CartLineExtensionProperties {
    PriceBeatCompetitorPrice = 'Memx_PriceBeatCompetitorPrice'
}

export interface IMmxComponentProps<T> extends IComponentProps<T> {
    telemetryContent?: ITelemetryContent;
}

/**
 * Assigns given pickup location to given product.
 */
export const setPickupLocationForProduct = async (
    product: SimpleProduct,
    quantity: number,
    onLocationSelected: (orgUnitLocation: OrgUnitLocation, deliveryMode: string) => Promise<void>,
    storeSelectorId: string,
    modalCloseButtonReference: React.RefObject<HTMLButtonElement>,
    channelId: number,
    actionContext: IActionContext,
    telemetry?: ITelemetry
): Promise<undefined> => {
    const storeSelectorStateManager = await storeSelectorStateDataAction(
        new GenericInput<IStoreSelectorStateManager>('mmx_mapSSSM', new MapStoreSelectorStateManager(), 'IStoreSelectorStateManager'),
        actionContext
    ).catch((error: Error) => {
        if (telemetry) {
            telemetry.error(error.message);
            telemetry.debug('Error finding store selector state manager');
        }
    });

    if (storeSelectorStateManager) {
        const deliveryOptions = await getDeliveryOptionsForSelectedVariant(
            new GetDeliveryOptionsForSelectedVariantInput(product.RecordId, channelId, undefined, undefined, true),
            actionContext
        );

        const locations = await getOrgUnitLocationsByArea(
            new GetOrgUnitLocationsByAreaInput(undefined, undefined, 0, 0, true),
            actionContext
        );

        if (locations && locations.length) {
            if (locations.length > 1) {
                await storeSelectorStateManager
                    .openDialog({
                        product: product,
                        id: storeSelectorId ?? 'mmx-store-selector',
                        parentElementRef: modalCloseButtonReference,
                        deliveryOptions: deliveryOptions,
                        additionalProperties: {
                            amountSelected: quantity,
                            isStockedProuct: product.Behavior?.IsStockedProduct
                        },
                        onLocationSelected: onLocationSelected
                    })
                    .catch((error: any) => {
                        if (telemetry) {
                            telemetry.warning(error);
                            telemetry.debug('[order-template-lines] Error trying to Update Cart Line from the order template lines');
                        }
                    });
                return Promise.resolve(undefined);
            } else {
                if (locations[0].OrgUnitAvailability && locations[0].OrgUnitAvailability?.OrgUnitLocation) {
                    const defaultDeliveryOption = deliveryOptions?.DeliveryOptions ? deliveryOptions.DeliveryOptions[0].Code : undefined;
                    if (defaultDeliveryOption) {
                        onLocationSelected(locations[0].OrgUnitAvailability.OrgUnitLocation, defaultDeliveryOption).catch(
                            (error: Error) => {
                                if (telemetry) {
                                    telemetry.warning(error.message);
                                    telemetry.debug('[utils] onLocationSelected callback ran into an error');
                                }
                            }
                        );
                        return Promise.resolve(undefined);
                    } else {
                        return Promise.reject('Could not find value delivery options');
                    }
                } else {
                    return Promise.reject('No valid org unit availability object');
                }
            }
        } else {
            return Promise.reject('No location found');
        }
    }
    return Promise.reject('Could not set pickup location');
};
/**
 * Utility function to extract the active productId in the following priority:
 * First query param (productId), then UrlToken (itemId), then module config.
 * Shamelessly stolen from msdyn commerce modules.
 * @param inputData The Action Input data.
 */
export const getSelectedProductIdFromActionInput = (inputData: ICreateActionContext<IGeneric<IAny>>): string | undefined => {
    if (inputData && inputData.requestContext && inputData.requestContext.query && inputData.requestContext.query['product-id']) {
        return inputData.requestContext.query['product-id'];
    } else if (inputData && inputData.requestContext && inputData.requestContext.query && inputData.requestContext.query.productId) {
        return inputData.requestContext.query.productId;
    } else if (
        inputData &&
        inputData.requestContext &&
        inputData.requestContext.urlTokens &&
        inputData.requestContext.urlTokens.pageType &&
        inputData.requestContext.urlTokens.pageType !== 'Category' &&
        inputData.requestContext.urlTokens.itemId
    ) {
        return inputData.requestContext.urlTokens.itemId;
    } else if (inputData && inputData.config && inputData.config.productId) {
        return inputData.config.productId;
    }
    return undefined;
};

/**
 * Generates a Image URL for a product based on data return from retail server.
 * @param product The Product returned by Retail Server.
 * @param apiSettings
 * @param trimDimensionsFromProductImageUrl optional parameter from config. If not set to true, use default url functionality.
 */
export const generateProductImageUrl = (
    product: SimpleProduct | ProductSearchResult,
    apiSettings: ICommerceApiSettings,
    trimDimensionsFromProductImageUrl?: boolean
): string | undefined => {
    if (!trimDimensionsFromProductImageUrl) {
        return defaultGenerateProductImageUrl(product, apiSettings);
    }
    const fullImagePath = /^(\/\/|^)http/;
    const PRODUCT_DELINEATOR_INDEX = 0;
    const PRODUCT_SEQ_INDEX = 4;

    const imageUrl = product.PrimaryImageUrl;
    if (imageUrl) {
        if (fullImagePath.test(imageUrl)) {
            return imageUrl;
        }
        const imageUrlDecoded = decodeURIComponent(imageUrl);
        const imageComponents = imageUrlDecoded.split(' ^');
        let imageUrlRebuilt = imageComponents[+PRODUCT_DELINEATOR_INDEX];
        if (imageComponents.length > 1) {
            imageUrlRebuilt += imageComponents[+PRODUCT_SEQ_INDEX];
        }
        // Images hosted in Retail Server must be encoded and joined with the base image url
        return apiSettings.baseImageUrl + encodeURIComponent(imageUrlRebuilt);
    }
    return undefined;
};

/**
 * Gets item at provided cache key from the cache of a state data action. If multiple results are found, return only the first one.
 * @param context The request context
 * @param cacheObjectType The object type returned by the state data action
 * @param cacheKey The name of the state data action
 */
export function getSingleItemFromStateAction<T>(context: IActionContext, cacheObjectType: string, cacheKey: string): T | undefined {
    const result = context.get<T>(cacheObjectType, cacheKey);
    if (!result) {
        return undefined;
    } else if (Object.prototype.toString.call(result) !== '[object Array]') {
        //@ts-ignore
        return result;
    }
    return result[0];
}

/**
 * Gets the current URL of the page from the request context.
 * @param requestContext The request context.
 */
export const getCurrentUrl = (requestContext: IRequestContext): URL => {
    if (MsDyn365.isBrowser) {
        return new URL(window.location.href);
    }

    // NOTE: Typing on requestURL is incorrect
    if (requestContext.url.requestUrl.href) {
        return new URL(requestContext.url.requestUrl.href);
    }
    return new URL(<string>(<unknown>requestContext.url.requestUrl));
};
